if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          navigation: this.querySelector('.product__media-navigation'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.addEventListener('keydown', this.onKeydown.bind(this));
        this.elements.navigation?.querySelectorAll('[data-gallery-nav]').forEach((button) => {
          button.addEventListener('click', () => this.navigate(button.dataset.galleryNav));
        });

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }

        this.updateNavigation();
      }

      onKeydown(event) {
        const mediaToggle = event.target.closest('.product__media-toggle');
        if (!mediaToggle || (event.key !== 'Enter' && event.key !== ' ')) return;

        event.preventDefault();
        mediaToggle.click();
      }

      onSlideChanged(event) {
        const activeMedia = event.detail.currentElement;
        if (!activeMedia) return;

        if (!activeMedia.classList.contains('is-active')) {
          this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
            element.classList.toggle('is-active', element === activeMedia);
          });
          this.playActiveMedia(activeMedia);
        }

        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${activeMedia.dataset.mediaId}"]`
          );
          this.setActiveThumbnail(thumbnail);
        }
        this.updateNavigation();
      }

      getMediaItems() {
        return Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]')).filter(
          (item, index) => index === 0 || !item.classList.contains('product__media-item--variant')
        );
      }

      navigate(direction) {
        const mediaItems = this.getMediaItems();
        const activeIndex = mediaItems.findIndex((item) => item.classList.contains('is-active'));
        const offset = direction === 'previous' ? -1 : 1;
        const nextItem = mediaItems[activeIndex + offset];

        if (!nextItem) return;
        this.setActiveMedia(nextItem.dataset.mediaId, false);
      }

      updateNavigation() {
        if (!this.elements.navigation) return;

        const mediaItems = this.getMediaItems();
        const activeIndex = mediaItems.findIndex((item) => item.classList.contains('is-active'));
        const previousButton = this.elements.navigation.querySelector('[data-gallery-nav="previous"]');
        const nextButton = this.elements.navigation.querySelector('[data-gallery-nav="next"]');

        previousButton.disabled = activeIndex <= 0;
        nextButton.disabled = activeIndex === -1 || activeIndex >= mediaItems.length - 1;
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.updateNavigation();

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        const deferredMedia = activeItem.querySelector('.deferred-media');
        // Browsing still images only stops media in this gallery.
        window.pauseAllMedia(deferredMedia ? document : this);
        if (!deferredMedia) return;

        if (!deferredMedia.hasNativeVideo()) {
          deferredMedia.loadContent(false);
          return;
        }

        deferredMedia
          .prepareVideo(false)
          .then((video) => {
            if (!video || !activeItem.classList.contains('is-active')) return;
            video.play().catch(() => {});
          })
          .catch(() => {});
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
